import { appConfigStore } from 'Models/AppConfigStore';
import { transactionStatus } from 'Models/Enums';
import { usePosProductList } from 'Modules/Products/Hooks/usePosProductList';
import { useProductList } from 'Modules/Products/Hooks/useProductList';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { exportTransactionsWithFilters, exportSelectedTransactions } from 'Services/Api/_HumanWritten/ReportService';
import { isNotNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { CustomTooltip } from 'Views/Components/_HumanWritten/CustomTooltip/CustomTooltip';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import {
	ClearFiltersButton,
	countActiveFilters,
} from 'Views/Components/_HumanWritten/StaffTables/Shared/ClearFiltersButton';
import { StaffFiltersDatePicker } from 'Views/Components/_HumanWritten/StaffTables/StaffFiltersDatePicker';
import { TransactionStatusTag } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionStatusTag';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import { store } from 'Models/Store';
import If from 'Views/Components/If/If';
import {
	defaultFilters as defaultTransactionFilters,
	transactionActionFilter,
	transactionListStatus,
	transactionTypeFilter,
	useStaffTransactionsStore,
} from './StaffTransactionsFilterStore';
import { StaffTransactionsColumnSelector } from './StaffTransactionsColumnSelector';
import { anytimeTicketLabel } from 'Util/_HumanWritten/anytimeTicketTerm';

export interface StaffTransactionsFiltersProps {
	setRefresh: () => void;
	totalTransactionCount: number;
}

const MAX_TRANSACTION_COUNT = 1500;

const StaffTransactionFilters = ({ setRefresh, totalTransactionCount }: StaffTransactionsFiltersProps) => {
	const intl = useIntl();

	// Get everything from Zustand store - single source of truth
	const {
		filters,
		selectedStatuses,
		selectedLocations,
		selectedCreatedBy,
		selectedTypes,
		selectedActions,
		setSelectedStatuses,
		setSelectedLocations,
		setSelectedCreatedBy,
		setSelectedTypes,
		setSelectedActions,
		setSearchString,
		updateFilters,
		resetFilters,
		visibleColumns,
		selectedTransactionIds,
		isExporting,
		setIsExporting,
	} = useStaffTransactionsStore();

	// Get initial search value: URL param takes priority, then store value
	const [tempSelectedStatuses, setTempSelectedStatuses] = useState<transactionListStatus[]>([]);
	const [tempSearchString, setTempSearchString] = useState(
		() => new URLSearchParams(window.location.search).get('search') || filters.searchString || '',
	);
	const [tempSelectedLocations, setTempSelectedLocations] = useState<string[]>([]);
	const [tempSelectedCreatedBy, setTempSelectedCreatedBy] = useState<string[]>([]);
	const [tempSelectedTypes, setTempSelectedTypes] = useState<transactionTypeFilter[]>([]);
	const [tempSelectedActions, setTempSelectedActions] = useState<transactionActionFilter[]>([]);

	const statusOptions: { name: string; value: transactionListStatus }[] = [
		{ name: 'In Progress', value: 'IN_PROGRESS' },
		{ name: intl.formatMessage({ id: 'booking-status__paid__label', defaultMessage: 'Paid' }), value: 'COMPLETED' },
		{ name: intl.formatMessage({ id: 'pos__status__label', defaultMessage: 'POS' }), value: 'POS_PAYMENT' },
		{ name: intl.formatMessage({ id: 'eftpos__name__label', defaultMessage: 'Eftpos' }), value: 'EFTPOS' },
		{ name: intl.formatMessage({ id: 'cash-payment-option__label', defaultMessage: 'Cash' }), value: 'CASH' },
		{
			name: intl.formatMessage({ id: 'booking-status__awaiting__label', defaultMessage: 'Awaiting' }),
			value: 'PENDING_INVOICE',
		},
		{
			name: intl.formatMessage({ id: 'booking-status__invoiced__label', defaultMessage: 'Invoiced' }),
			value: 'INVOICED',
		},
		// Only offer the Unpaid filter when the pay-later feature is surfaced (see ShowUnpaidStatusTag).
		...(appConfigStore.ShowUnpaidStatusTag
			? [
					{
						name: intl.formatMessage({ id: 'booking-status__unpaid__label', defaultMessage: 'Unpaid' }),
						value: 'PENDING_PAYMENT' as transactionListStatus,
					},
				]
			: []),
		{ name: 'Abandoned', value: 'ABANDONED' },
		{ name: 'Failed', value: 'FAILED' },
	];

	const locationOptions = [
		{ name: 'Check-in', value: 'CHECK_IN' },
		{ name: 'Booking Wizard', value: 'BOOKING_WIZARD' },
	];

	const createdByOptions = [
		{ name: 'Staff', value: 'Staff' },
		{ name: 'Customer', value: 'Customer' },
		{ name: 'System', value: 'System' },
	];

	// POS and anytime tickets are only relevant when the merchant actually sells them: for anytime
	// tickets the feature flag is enabled or at least one product exists; for POS, at least one POS
	// product exists (a merchant may still have historical transactions after disabling a feature).
	// Ask for a single record just to read the total count.
	const { data: posProductList } = usePosProductList(1, 1);
	const { data: anytimeProductList } = useProductList(1, 1);
	const hasPosProducts = (posProductList?.total ?? 0) > 0;
	const hasAnytimeProducts = (anytimeProductList?.total ?? 0) > 0;
	const showPosType = hasPosProducts;
	const showAnytimeType = appConfigStore.anytimeTicketingEnabled || hasAnytimeProducts;

	// Only offer type options that are relevant to this merchant. Ferry bookings can always occur;
	// everything else is gated on its feature flag / config. Note that staff can sell a voucher even
	// with customer purchase switched off, so a merchant with the flag off may still hold historical
	// voucher-purchase rows — those stay visible under the default "All", they just can't be filtered
	// to on their own.
	const typeOptions: { name: string; value: transactionTypeFilter }[] = [
		{ name: 'Ferry Booking', value: 'FERRY_BOOKING' },
		...(appConfigStore.eventsEnabled
			? [{ name: 'Event Booking', value: 'EVENT_BOOKING' as transactionTypeFilter }]
			: []),
		...(appConfigStore.parkingEnabled ? [{ name: 'Parking', value: 'PARKING' as transactionTypeFilter }] : []),
		...(appConfigStore.voucherPurchaseEnabled
			? [{ name: 'Voucher Purchase', value: 'VOUCHER_PURCHASE' as transactionTypeFilter }]
			: []),
		...(showAnytimeType ? [{ name: anytimeTicketLabel(), value: 'ANYTIME_TICKET' as transactionTypeFilter }] : []),
		...(showPosType ? [{ name: 'POS', value: 'POS' as transactionTypeFilter }] : []),
		...(appConfigStore.charterEnabled
			? [{ name: 'Charter', value: 'CHARTER' as transactionTypeFilter }]
			: []),
	];

	const actionOptions: { name: string; value: transactionActionFilter }[] = [
		{ name: 'Creation', value: 'CREATION' },
		{ name: 'Alteration', value: 'ALTERATION' },
		{ name: 'Cancellation', value: 'CANCELLATION' },
	];

	// Sync temp state when store state changes
	useEffect(() => {
		setTempSelectedStatuses(selectedStatuses);
	}, [selectedStatuses]);

	useEffect(() => {
		setTempSelectedLocations(selectedLocations);
	}, [selectedLocations]);

	useEffect(() => {
		setTempSelectedCreatedBy(selectedCreatedBy);
	}, [selectedCreatedBy]);

	// An empty stored value means "all" (no type filter), so reflect that as every visible option
	// being checked in the popover.
	useEffect(() => {
		setTempSelectedTypes(selectedTypes.length === 0 ? typeOptions.map(o => o.value) : selectedTypes);
	}, [selectedTypes]);

	// The POS and Anytime Ticket options only appear once their gating queries (usePosProductList /
	// useProductList) resolve, which is usually AFTER this component first renders. The effect above
	// only re-runs when the stored selection changes, so without this the popover keeps a temp
	// selection built from the smaller option list. `onAfterClose` then compares that stale list
	// against the now-larger typeOptions, decides "not everything is selected", and commits an
	// explicit type filter — silently dropping POS from a filter that displayed as "All".
	//
	// So when the option list grows, extend a temp selection that covered every previously visible
	// option. A selection the user has genuinely narrowed is left alone.
	const typeOptionValues = typeOptions.map(o => o.value);
	const serialisedTypeOptionValues = typeOptionValues.join(',');
	const previousTypeOptionValuesRef = React.useRef(typeOptionValues);
	useEffect(() => {
		const previousValues = previousTypeOptionValuesRef.current;
		previousTypeOptionValuesRef.current = typeOptionValues;

		if (arraysEqual(previousValues, typeOptionValues)) {
			return;
		}

		setTempSelectedTypes(currentTemp =>
			previousValues.every(value => currentTemp.includes(value)) ? typeOptionValues : currentTemp,
		);
		// typeOptionValues is compared by value through serialisedTypeOptionValues; including the
		// array itself would re-run this on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [serialisedTypeOptionValues]);

	// An empty stored value means "all" (no action filter).
	useEffect(() => {
		setTempSelectedActions(selectedActions.length === 0 ? actionOptions.map(o => o.value) : selectedActions);
	}, [selectedActions]);

	// On mount: if URL param exists, apply to store and clean URL. A link here names one transaction,
	// so every status is shown: the default filter hides abandoned and failed ones, which is what an
	// expired cart's transaction is.
	useEffect(() => {
		const urlSearch = new URLSearchParams(window.location.search).get('search');
		if (urlSearch) {
			setSearchString(urlSearch);
			setSelectedStatuses(statusOptions.map(option => option.value));
			setRefresh();
			const url = new URL(window.location.href);
			url.searchParams.delete('search');
			window.history.replaceState({}, '', url.pathname + url.search);
		}
	}, []);

	// Debounce: user typing → store (one-way only, no store→temp sync)
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (tempSearchString !== (filters.searchString || '')) {
				setSearchString(tempSearchString);
				setRefresh();
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [tempSearchString]);

	const arraysEqual = (arr1: any[], arr2: any[]) => {
		if (arr1.length !== arr2.length) return false;
		const sorted1 = [...arr1].sort();
		const sorted2 = [...arr2].sort();
		return sorted1.every((value, index) => value === sorted2[index]);
	};

	const onAfterClose = () => {
		// Use local variables to avoid React async state issues.
		// If user unchecked everything, discard the change and revert to the store value.
		const effectiveStatuses = tempSelectedStatuses.length === 0 ? selectedStatuses : tempSelectedStatuses;
		const effectiveLocations = tempSelectedLocations.length === 0 ? selectedLocations : tempSelectedLocations;
		const effectiveCreatedBy = tempSelectedCreatedBy.length === 0 ? selectedCreatedBy : tempSelectedCreatedBy;

		// Types: "all visible selected" is stored as [] (= no filter). An empty temp selection means
		// the user unchecked everything, which we discard and revert to the current stored value.
		const currentTypesAsTemp = selectedTypes.length === 0 ? typeOptions.map(o => o.value) : selectedTypes;
		const effectiveTypesTemp = tempSelectedTypes.length === 0 ? currentTypesAsTemp : tempSelectedTypes;
		const effectiveTypes: transactionTypeFilter[] =
			effectiveTypesTemp.length === typeOptions.length ? [] : effectiveTypesTemp;

		// Actions: same "all selected = [] (no filter)" convention as types.
		const currentActionsAsTemp = selectedActions.length === 0 ? actionOptions.map(o => o.value) : selectedActions;
		const effectiveActionsTemp = tempSelectedActions.length === 0 ? currentActionsAsTemp : tempSelectedActions;
		const effectiveActions: transactionActionFilter[] =
			effectiveActionsTemp.length === actionOptions.length ? [] : effectiveActionsTemp;

		// Reset temp state to effective values
		if (tempSelectedStatuses.length === 0) {
			setTempSelectedStatuses(selectedStatuses);
		}
		if (tempSelectedLocations.length === 0) {
			setTempSelectedLocations(selectedLocations);
		}
		if (tempSelectedCreatedBy.length === 0) {
			setTempSelectedCreatedBy(selectedCreatedBy);
		}
		if (tempSelectedTypes.length === 0) {
			setTempSelectedTypes(currentTypesAsTemp);
		}
		if (tempSelectedActions.length === 0) {
			setTempSelectedActions(currentActionsAsTemp);
		}

		const statusesChanged = !arraysEqual(effectiveStatuses, selectedStatuses);
		const locationsChanged = !arraysEqual(effectiveLocations, selectedLocations);
		const createdByChanged = !arraysEqual(effectiveCreatedBy, selectedCreatedBy);
		const typesChanged = !arraysEqual(effectiveTypes, selectedTypes);
		const actionsChanged = !arraysEqual(effectiveActions, selectedActions);

		// Apply changes if anything changed
		if (statusesChanged || locationsChanged || createdByChanged || typesChanged || actionsChanged) {
			if (statusesChanged) {
				setSelectedStatuses(effectiveStatuses);
			}
			if (locationsChanged) {
				setSelectedLocations(effectiveLocations);
			}
			if (createdByChanged) {
				setSelectedCreatedBy(effectiveCreatedBy);
			}
			if (typesChanged) {
				setSelectedTypes(effectiveTypes);
			}
			if (actionsChanged) {
				setSelectedActions(effectiveActions);
			}

			// Trigger refresh after state has settled
			requestAnimationFrame(() => {
				setRefresh();
			});
		}
	};

	const getStatusDisplay = () => {
		if (selectedStatuses.length === 0 || selectedStatuses.length === statusOptions.length) {
			return 'All';
		}
		const tags: JSX.Element[] = [];
		selectedStatuses.forEach((status, index) => {
			tags.push(<TransactionStatusTag key={status} status={status as transactionStatus} />);
		});
		return tags;
	};

	const getLocationDisplay = () => {
		if (selectedLocations.length === 0 || selectedLocations.length === locationOptions.length) {
			return 'All';
		}

		const tags = selectedLocations
			.map(loc => locationOptions.find(opt => opt.value === loc))
			.filter(opt => isNotNullOrUndefined(opt))
			.map((opt, index) => <Tag key={opt!.value} text={opt!.name} tagClassName="grey" />);

		return <>{tags}</>;
	};

	const handleDatePickerChange = (startDate: Date | undefined, endDate: Date | undefined) => {
		updateFilters({
			startDate,
			endDate,
			pageNo: 0,
		});
		setRefresh();
	};

	const handleFilterTypeChange = (filterByCreatedDate: boolean) => {
		updateFilters({
			filterByCreatedDate,
			pageNo: 0,
		});
		setRefresh();
	};

	const getCreatedByDisplay = () => {
		if (selectedCreatedBy.length === 0 || selectedCreatedBy.length === createdByOptions.length) {
			return 'All';
		}

		const tags = selectedCreatedBy
			.map(cb => createdByOptions.find(opt => opt.value === cb))
			.filter(opt => isNotNullOrUndefined(opt))
			.map(opt => <Tag key={opt!.value} text={opt!.name} tagClassName="grey" />);

		return <>{tags}</>;
	};

	const getTypeDisplay = () => {
		// Empty stored value or every visible option selected both mean "All".
		if (selectedTypes.length === 0 || selectedTypes.length === typeOptions.length) {
			return 'All';
		}

		const tags = selectedTypes
			.map(type => typeOptions.find(opt => opt.value === type))
			.filter(opt => isNotNullOrUndefined(opt))
			.map(opt => <Tag key={opt!.value} text={opt!.name} tagClassName="grey" />);

		return <>{tags}</>;
	};

	const getActionDisplay = () => {
		if (selectedActions.length === 0 || selectedActions.length === actionOptions.length) {
			return 'All';
		}

		const tags = selectedActions
			.map(action => actionOptions.find(opt => opt.value === action))
			.filter(opt => isNotNullOrUndefined(opt))
			.map(opt => <Tag key={opt!.value} text={opt!.name} tagClassName="grey" />);

		return <>{tags}</>;
	};

	const isAllStatusesSelected = tempSelectedStatuses.length === statusOptions.length;
	const isAllLocationsSelected = tempSelectedLocations.length === locationOptions.length;
	const isAllCreatedBySelected = tempSelectedCreatedBy.length === createdByOptions.length;
	const isAllTypesSelected = tempSelectedTypes.length === typeOptions.length;
	const isAllActionsSelected = tempSelectedActions.length === actionOptions.length;

	// Calculate export count based on current state
	const count = selectedTransactionIds.size > 0 ? selectedTransactionIds.size : totalTransactionCount;

	const handleExport = async () => {
		setIsExporting(true);
		try {
			const columns = [
				...visibleColumns.filter(col => col.visible).map(col => col.key),
				'location', // Always include location in exports
				'tripDate', // Trip date/s — export only, not a table column
				'route', // Route — export only, not a table column
				'paidDate', // Paid date/time — export only, not a table column
				'takenBy', // Who took the payment — export only, not a table column
			];
			if (selectedTransactionIds.size > 0) {
				await exportSelectedTransactions(Array.from(selectedTransactionIds), columns);
			} else {
				await exportTransactionsWithFilters({
					searchString: filters.searchString || undefined,
					startDate: filters.startDate,
					endDate: filters.endDate,
					filterByCreatedDate: filters.filterByCreatedDate,
					statusFilters: filters.statusFilters,
					locationFilters: filters.locationFilters,
					createdByFilters: filters.createdByFilters,
					typeFilters: filters.typeFilters,
					actionFilters: filters.actionFilters,
					columns,
				});
			}
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div className="table-filters__container">
			<div className="left">
				{/* Created Date Range */}
				<div data-test-id="transactions-filter-created-date">
					<StaffFiltersDatePicker
						options={['Created Date']}
						startDate={filters.startDate}
						endDate={filters.endDate}
						filterByCreatedDate={filters.filterByCreatedDate}
						onDateChange={handleDatePickerChange}
						onFilterTypeChange={handleFilterTypeChange}
					/>
				</div>

				{/* Status Filter */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonTestId="transactions-filter-status"
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Status</p>
								<span className="tags">{getStatusDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: isAllStatusesSelected }}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedStatuses([]);
									} else {
										setTempSelectedStatuses(statusOptions.map(option => option.value));
									}
								}}
							/>
							{statusOptions.map(statusOption => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={statusOption.value}
										model={{ value: tempSelectedStatuses.includes(statusOption.value) }}
										modelProperty="value"
										label={statusOption.name}
										onChecked={(_, value) => {
											if (value) {
												const newStatuses = [...tempSelectedStatuses, statusOption.value];
												setTempSelectedStatuses(newStatuses);
											} else {
												setTempSelectedStatuses(
													tempSelectedStatuses.filter(x => x !== statusOption.value),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Location Filter */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonTestId="transactions-filter-location"
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Location</p>
								<span className="tags">{getLocationDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: isAllLocationsSelected }}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedLocations([]);
									} else {
										setTempSelectedLocations(locationOptions.map(option => option.value));
									}
								}}
							/>
							{locationOptions.map(locationOption => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={locationOption.value}
										model={{ value: tempSelectedLocations.includes(locationOption.value) }}
										modelProperty="value"
										label={locationOption.name}
										onChecked={(_, value) => {
											if (value) {
												const newLocations = [...tempSelectedLocations, locationOption.value];
												setTempSelectedLocations(newLocations);
											} else {
												setTempSelectedLocations(
													tempSelectedLocations.filter(x => x !== locationOption.value),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Created By Filter */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonTestId="transactions-filter-created-by"
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Created By</p>
								<span className="tags">{getCreatedByDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: isAllCreatedBySelected }}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedCreatedBy([]);
									} else {
										setTempSelectedCreatedBy(createdByOptions.map(option => option.value));
									}
								}}
							/>
							{createdByOptions.map(createdByOption => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={createdByOption.value}
										model={{ value: tempSelectedCreatedBy.includes(createdByOption.value) }}
										modelProperty="value"
										label={createdByOption.name}
										onChecked={(_, value) => {
											if (value) {
												const newCreatedBy = [...tempSelectedCreatedBy, createdByOption.value];
												setTempSelectedCreatedBy(newCreatedBy);
											} else {
												setTempSelectedCreatedBy(
													tempSelectedCreatedBy.filter(x => x !== createdByOption.value),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Type Filter */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonTestId="transactions-filter-type"
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Type</p>
								<span className="tags">{getTypeDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: isAllTypesSelected }}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedTypes([]);
									} else {
										setTempSelectedTypes(typeOptions.map(option => option.value));
									}
								}}
							/>
							{typeOptions.map(typeOption => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={typeOption.value}
										model={{ value: tempSelectedTypes.includes(typeOption.value) }}
										modelProperty="value"
										label={typeOption.name}
										onChecked={(_, value) => {
											if (value) {
												const newTypes = [...tempSelectedTypes, typeOption.value];
												setTempSelectedTypes(newTypes);
											} else {
												setTempSelectedTypes(
													tempSelectedTypes.filter(x => x !== typeOption.value),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Action Filter */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonTestId="transactions-filter-action"
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Action</p>
								<span className="tags">{getActionDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: isAllActionsSelected }}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedActions([]);
									} else {
										setTempSelectedActions(actionOptions.map(option => option.value));
									}
								}}
							/>
							{actionOptions.map(actionOption => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={actionOption.value}
										model={{ value: tempSelectedActions.includes(actionOption.value) }}
										modelProperty="value"
										label={actionOption.name}
										onChecked={(_, value) => {
											if (value) {
												const newActions = [...tempSelectedActions, actionOption.value];
												setTempSelectedActions(newActions);
											} else {
												setTempSelectedActions(
													tempSelectedActions.filter(x => x !== actionOption.value),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Search Input */}
				<form onSubmit={e => e.preventDefault()}>
					<div className="search-box">
						<input
							id="booking-search-input"
							data-test-id="transactions-search-input"
							placeholder="Search"
							className="fh-basic-search"
							autoComplete="off"
							value={tempSearchString}
							onChange={e => setTempSearchString(e.target.value)}
						/>
						<If condition={stringIsEmpty(tempSearchString)}>
							<CustomTooltip
								id="search-tool-tip"
								content={`Search by Transaction ID, Customer Name and Email, Booking ID, Voucher Code, ${anytimeTicketLabel()} Code${
									appConfigStore.charterEnabled ? ', Charter Name' : ''
								}`}
								toolTipPosition="bottom-right"
							/>
						</If>
						<If condition={stringNotEmpty(tempSearchString)}>
							<Button
								className="clear-search"
								onClick={e => {
									e.preventDefault();
									setTempSearchString('');
								}}
								colors={Colors.Black}
								display={Display.Text}
							>
								<Icon name="cross" />
							</Button>
						</If>
					</div>
				</form>
				<ClearFiltersButton
					activeCount={countActiveFilters(filters, defaultTransactionFilters)}
					onClear={() => {
						resetFilters();
						setTempSearchString('');
						setRefresh();
					}}
					dataTestId="transactions-clear-filters"
				/>
			</div>
			<div className="right">
				<StaffTransactionsColumnSelector />
				<If condition={store.can('ExportOperationalReports')}>
					<Button
						display={Display.Solid}
						colors={Colors.Secondary}
						className="export-button"
						newStyles
						onClick={handleExport}
						disabled={isExporting || (selectedTransactionIds.size === 0 && totalTransactionCount === 0)}
						buttonProps={{ 'data-test-id': 'transactions-export-button' } as any}
					>
						<Icon name="download" useMaterialIcons />
						{isExporting ? 'Exporting...' : 'Export'} ({count})
					</Button>
				</If>
			</div>
		</div>
	);
};

export default StaffTransactionFilters;
